import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock, Hash, ArrowRight } from 'lucide-react';
import { loginThunk, clearError } from '../store/slices/auth';
import tenant from '../config/tenant';

export default function LoginPage() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const location  = useLocation();
  const { loading, error, isAuthenticated } = useSelector((s) => s.auth);

  const [form, setForm]             = useState({ serviceNo: '', password: '' });
  const [showPw, setShowPw]         = useState(false);
  const [focusedField, setFocused]  = useState(null);


  const redirectTo = location.state?.from || '/dashboard';

  // If already authenticated (e.g. back-button after login) go straight in
  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, navigate, redirectTo]);

  // Clear any stale error when the component unmounts
  useEffect(() => () => { dispatch(clearError()); }, [dispatch]);

  const handleChange = (field) => (e) => {
    if (error) dispatch(clearError());           // clear error on first keystroke
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.serviceNo.trim() || !form.password.trim()) return;

    try {
      // unwrap() re-throws the rejectWithValue payload as an error so we can
      // catch it here if needed; the slice also sets state.error automatically.
      await dispatch(loginThunk({ serviceNo: form.serviceNo.trim(), password: form.password })).unwrap();
      navigate(redirectTo, { replace: true });
    } catch {
      // Error is already stored in state.auth.error by the slice — nothing to do here
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 relative overflow-hidden p-6">

      {/* ── Animated background ─────────────────────────────────────────────── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-40 w-96 h-96 bg-primary-500/20 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-0 -right-40 w-96 h-96 bg-primary-500/15 rounded-full blur-[120px] animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary-500/5 rounded-full blur-[140px]" />
        <div className="absolute top-20 left-10 opacity-5">
          <div className="w-32 h-32 border-4 border-primary-400 rounded-lg rotate-12" />
        </div>
        <div className="absolute bottom-20 right-10 opacity-5">
          <div className="w-48 h-48 border-4 border-primary-400 rounded-full" />
        </div>
      </div>

      {/* ── Card container ──────────────────────────────────────────────────── */}
      <div className="relative z-10 w-full max-w-md">


        <div className="text-center mb-8">
          {tenant.branding.logoOnLightChip ? (
            <div className="inline-block bg-white/95 rounded-2xl px-6 py-4 border border-primary-500/50 shadow-2xl shadow-primary-500/20 mb-4">
              <img src={tenant.branding.logoUrl} alt="Logo" className="h-16 w-auto object-contain" />
            </div>
          ) : (
            <div className="inline-block bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-3 border border-primary-500/30 shadow-2xl mb-4">
              <img src={tenant.branding.logoUrl} alt="Logo" className="h-14 w-auto object-contain" />
            </div>
          )}
        </div>

        {/* Login card */}
        <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-xl rounded-2xl border border-gray-700 shadow-2xl overflow-hidden">
          {/* Green accent line */}
          <div className="h-1 bg-gradient-to-r from-primary-500/0 via-primary-500 to-primary-500/0" />

          <div className="p-8">
            <div className="mb-8 text-center">
              <h2 className="text-white font-bold text-2xl mb-2">Welcome Back</h2>
              <p className="text-gray-400 text-sm">{tenant.branding.loginSubtitle}</p>
            </div>

            {/* ── Error banner ────────────────────────────────────────────── */}
            {error && (
              <div key={error} className="bg-red-500/10 backdrop-blur-sm border-l-4 border-red-500 rounded-lg px-4 py-3 mb-6 animate-shake">
                <p className="text-red-400 text-sm font-medium">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">

              {/* ── Service Number ─────────────────────────────────────────── */}
              <div className="space-y-1.5">
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-300 uppercase tracking-wider">
                  <Hash size={12} />
                  Service Number
                </label>
                <div className={`relative transition-all duration-200 ${focusedField === 'serviceNo' ? 'transform scale-[1.01]' : ''}`}>
                  <Hash
                    size={16}
                    className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors duration-200 ${
                      focusedField === 'serviceNo' ? 'text-primary-400' : 'text-gray-500'
                    }`}
                  />
                  <input
                    type="text"
                    value={form.serviceNo}
                    onFocus={() => setFocused('serviceNo')}
                    onBlur={() => setFocused(null)}
                    onChange={handleChange('serviceNo')}
                    placeholder="Enter your service number"
                    autoComplete="username"
                    disabled={loading}
                    className={`w-full bg-gray-900/50 backdrop-blur-sm border-2 rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none transition-all duration-200 disabled:opacity-50
                    ${focusedField === 'serviceNo' ? 'border-primary-500/50' : 'border-gray-600/50'}`}
                  /> 
                </div>
              </div>

              {/* ── Password ────────────────────────────────────────────────── */}
              <div className="space-y-1.5">
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-300 uppercase tracking-wider">
                  <Lock size={12} />
                  Password
                </label>
                <div className={`relative transition-all duration-200 ${focusedField === 'password' ? 'transform scale-[1.01]' : ''}`}>
                  <Lock
                    size={16}
                    className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors duration-200 ${
                      focusedField === 'password' ? 'text-primary-400' : 'text-gray-500'
                    }`}
                  />
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={form.password}
                    onFocus={() => setFocused('password')}
                    onBlur={() => setFocused(null)}
                    onChange={handleChange('password')}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={loading}
                    className={`w-full bg-gray-900/50 backdrop-blur-sm border-2 rounded-xl pl-10 pr-12 py-3 text-white text-sm placeholder-gray-600 focus:outline-none transition-all duration-200 disabled:opacity-50
                      ${focusedField === 'password' ? 'border-primary-500/50' : 'border-gray-600/50'}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary-400 transition-colors duration-200"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* ── Remember me ─────────────────────────────────────────────── */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 rounded border-gray-600 bg-gray-800 text-primary-500 focus:ring-primary-500 focus:ring-offset-0"
                  />
                  <span className="text-gray-400 text-xs group-hover:text-gray-300 transition-colors">Remember me</span>
                </label>
                <button type="button" className="text-primary-400 text-xs hover:text-primary-300 transition-colors">
                  Forgot password?
                </button>
              </div>

              {/* ── Submit ──────────────────────────────────────────────────── */}
              <button
                type="submit"
                disabled={loading || !form.serviceNo.trim() || !form.password.trim()}
                className="group relative w-full mt-2 overflow-hidden rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-surface-900 font-bold py-3.5 transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/25 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-primary-500 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                <span className="relative flex items-center justify-center gap-2 text-sm font-semibold">
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Authenticating...
                    </>
                  ) : (
                    <>
                      {tenant.branding.loginTitle}
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
                    </>
                  )}
                </span>
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-700">
              <div className="flex items-center justify-center gap-4 text-xs">
                <span className="text-gray-500">🔒 Secure Login</span>
                <span className="text-gray-600">•</span>
                <span className="text-gray-500">🛡️ 2FA Enabled</span>
                <span className="text-gray-600">•</span>
                <span className="text-gray-500">🔐 SSL Protected</span>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">
          © 2026 Dockyard Software
        </p>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25%       { transform: translateX(-5px); }
          75%       { transform: translateX(5px); }
        }
        .animate-shake { animation: shake 0.3s ease-in-out; }
        .delay-1000    { animation-delay: 1000ms; }
      `}</style>
    </div>
  );
}